import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import {
  TrendingUp,
  TrendingDown,
  Users,
  CreditCard,
  DollarSign,
  Search,
  Filter,
  Calendar,
  ChevronRight,
  ChevronLeft,
  Download,
  RefreshCw,
  User,
  Activity,
  Briefcase,
  Clock
} from 'lucide-react';

interface PlatinumHistory {
  _id: string;
  email: string;
  totalAmount: string;
  amount: string;
  subscription: string;
  incomeDate?: string;
  investDate?: string;
  action: string;
  type: string;
  actionDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface Filters {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  email?: string;
  subscription?: string;
  action?: string;
  type?: string;
  startDate?: string;
  endDate?: string;
}

const PlatinumHistoryDashboard: React.FC = () => {
  const [histories, setHistories] = useState<PlatinumHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    emails: [] as string[],
    subscriptions: [] as string[],
    actions: [] as string[],
    types: [] as string[]
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  const [summary, setSummary] = useState({
    totalTransactions: 0,
    totalCredit: 0,
    totalDebit: 0,
    netBalance: 0,
    uniqueUsersCount: 0
  });

  const [userSummary, setUserSummary] = useState<any[]>([]);

  const [filters, setFilters] = useState<Filters>({
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
    search: '',
    email: '',
    subscription: '',
    action: '',
    type: '',
    startDate: '',
    endDate: ''
  });

  const API_BASE_URL = `${API_BASE}/api/paltinumhistory`;

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await axios.get(`${API_BASE_URL}/admin/platinum-histories?${params}`);

      if (response.data.status === "success") {
        const data = response.data.data;
        setHistories(data.histories || []);
        setPagination(data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 20
        });
        setSummary(data.summary || {
          totalTransactions: 0,
          totalCredit: 0,
          totalDebit: 0,
          netBalance: 0,
          uniqueUsersCount: 0
        });
        setUserSummary(data.userSummary || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/platinum-histories/filters`);
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchFilterOptions();
  }, [filters]);

  const handleFilterChange = (key: keyof Filters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page }));
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    if (histories.length === 0) return;

    const headers = ['Email', 'Amount', 'Total Amount', 'Type', 'Action', 'Subscription', 'Investment Date', 'Created Date'];
    const rows = histories.map(history => [
      history.email,
      history.amount,
      history.totalAmount,
      history.type,
      history.action,
      history.subscription,
      formatDate(history.investDate),
      formatDateTime(history.createdAt)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `platinum-history-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sortBy: 'createdAt',
      sortOrder: 'desc',
      search: '',
      email: '',
      subscription: '',
      action: '',
      type: '',
      startDate: '',
      endDate: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-center md:text-left">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center justify-center md:justify-start gap-3">
              <Briefcase className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
              Platinum History
            </h1>
            <p className="text-sm md:text-base text-gray-600 mt-2">Track all platinum investment transactions</p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={histories.length === 0}
            className="w-full md:w-auto px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-5 h-5" />
            <span className="font-medium">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}


      {/* Filters Card */}
      <div className="bg-white rounded-2xl shadow-xl p-4 md:p-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Filter className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Filters & Search</h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={clearFilters}
              className="flex-1 md:flex-none px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors text-sm"
            >
              Clear
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex-1 md:flex-none px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 flex items-center justify-center gap-2 transition-all disabled:opacity-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Filters Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search emails, actions..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
            />
          </div>

          {/* Email Filter */}
          <div>
            <select
              value={filters.email}
              onChange={(e) => handleFilterChange('email', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
            >
              <option value="">All Users</option>
              {filterOptions.emails.map(email => (
                <option key={email} value={email}>{email}</option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
            >
              <option value="">All Types</option>
              <option value="credit">Credit</option>
              <option value="debit">Debit</option>
            </select>
          </div>

          {/* Action Filter */}
          <div>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
            >
              <option value="">All Actions</option>
              {filterOptions.actions.map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Date Range */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
              />
            </div>
          </div>
        </div>

        {/* Active Users */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-700">Active Users: {summary.uniqueUsersCount}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-500">Updated just now</span>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
              >
                <option value="20">20 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>
            </div>
          </div>
        </div>

        {/* Transactions Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dates
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mb-3" />
                      <p className="text-gray-600">Loading transactions...</p>
                    </div>
                  </td>
                </tr>
              ) : histories.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <CreditCard className="w-12 h-12 text-gray-400 mb-3" />
                      <p className="text-gray-500 text-lg font-medium">No transactions found</p>
                      <p className="text-gray-400 mt-1">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                histories.map((history) => (
                  <tr key={history._id} className="hover:bg-gray-50 transition-colors">
                    {/* User Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                            {history.email}
                          </div>
                          <div className="text-xs text-gray-500">
                            {history.subscription}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Transaction Column */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(history.amount)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Total: {formatCurrency(history.totalAmount)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        Action: <span className="font-medium">{history.action}</span>
                      </div>
                    </td>

                    {/* Type Column */}
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${history.type === 'credit'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {history.type === 'credit' ? (
                          <>
                            <TrendingUp className="w-3 h-3 mr-1" />
                            CREDIT
                          </>
                        ) : (
                          <>
                            <TrendingDown className="w-3 h-3 mr-1" />
                            DEBIT
                          </>
                        )}
                      </span>
                    </td>

                    {/* Details Column */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-gray-400" />
                          <span>Platinum Plan</span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          ID: {history._id.substring(0, 8)}...
                        </div>
                      </div>
                    </td>

                    {/* Dates Column */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span>{formatDate(history.createdAt)}</span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Invested: {formatDate(history.investDate)}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards View */}
        <div className="md:hidden divide-y divide-gray-50 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="px-6 py-12 text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
              <p className="text-gray-600 text-sm">Loading...</p>
            </div>
          ) : histories.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 font-medium text-sm">No transactions found</p>
            </div>
          ) : (
            histories.map((history) => (
              <div key={history._id} className="p-4 bg-white">
                <div className="flex justify-between items-start mb-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-gray-900 truncate">{history.email}</h3>
                    <p className="text-[10px] text-gray-400 font-medium">{formatDate(history.createdAt)}</p>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-tight ${history.type === 'credit'
                    ? 'bg-green-50 text-green-600'
                    : 'bg-red-50 text-red-600'
                    }`}>
                    {history.type}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-[11px] bg-gray-50/50 p-3 rounded-xl border border-gray-50">
                  <div>
                    <span className="block text-[9px] text-gray-400 uppercase font-bold mb-0.5">Amount</span>
                    <span className="text-gray-900 font-black text-sm">{formatCurrency(history.amount)}</span>
                  </div>
                  <div>
                    <span className="block text-[9px] text-gray-400 uppercase font-bold mb-0.5">Balance</span>
                    <span className="text-gray-700 font-semibold">{formatCurrency(history.totalAmount)}</span>
                  </div>
                  <div>
                    <span className="block text-[9px] text-gray-400 uppercase font-bold mb-0.5">Action</span>
                    <span className="text-blue-600 font-bold italic">{history.action}</span>
                  </div>
                  <div>
                    <span className="block text-[9px] text-gray-400 uppercase font-bold mb-0.5">Plan</span>
                    <span className="text-gray-600 font-medium">{history.subscription}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 md:px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-xs md:text-sm text-gray-700 order-2 md:order-1 text-center">
              Showing <span className="font-semibold">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> to{' '}
              <span className="font-semibold">
                {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
              </span> of{' '}
              <span className="font-semibold">{pagination.totalItems}</span>
            </div>
            <div className="flex items-center space-x-1 md:space-x-2 order-1 md:order-2">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="p-1 md:p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
              </button>

              <div className="flex items-center gap-1">
                {(() => {
                  const pages = [];
                  const maxVisible = window.innerWidth < 640 ? 3 : 5;
                  let start = Math.max(1, pagination.currentPage - Math.floor(maxVisible / 2));
                  let end = Math.min(pagination.totalPages, start + maxVisible - 1);

                  if (end - start + 1 < maxVisible) {
                    start = Math.max(1, end - maxVisible + 1);
                  }

                  for (let i = start; i <= end; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        className={`w-8 h-8 md:px-3 md:py-1 rounded-lg text-xs md:text-sm font-medium ${pagination.currentPage === i
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 hover:bg-gray-100 border border-transparent'
                          }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
              </div>

              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-1 md:p-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Top Users Section */}
      {/* {userSummary.length > 0 && (
        <div className="mt-8">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Top Platinum Investors
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {userSummary.slice(0, 6).map((user, index) => (
                <div key={user._id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 truncate max-w-[180px]">
                          {user._id}
                        </div>
                        <div className="text-xs text-gray-500">
                          {user.totalInvestments} transactions
                        </div>
                      </div>
                    </div>
                    <div className="text-lg font-bold text-blue-600">
                      {formatCurrency(user.totalAmount)}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 flex justify-between">
                    <div>
                      First: {formatDate(user.firstInvestment)}
                    </div>
                    <div>
                      Last: {formatDate(user.latestInvestment)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default PlatinumHistoryDashboard;